/** Which price figure a calculation tile reads from the drawer's `amounts` map. */
export type SuggestedPriceAmountKey = 'aiValue' | 'negotiationStartingPrice' | 'overrideValue';

/** Which discount figure a calculation tile reads from the drawer's `percents` map. */
export type SuggestedPricePercentKey = 'aiDiscount' | 'overrideDiscount';

/**
 * How a tile presents its figures:
 * - `currency` — the price alone
 * - `percent` — the green trend percentage alone, no price
 * - `stacked` — the price with the green trend percentage parenthesised beneath it
 */
export type SuggestedPriceStepRender = 'currency' | 'percent' | 'stacked';

export interface SuggestedPriceCalculationStep {
  amount?: SuggestedPriceAmountKey;
  /** Key under `bid_request.line_items.suggested_price.explanation` for the tile's title. */
  labelKey: string;
  percent?: SuggestedPricePercentKey;
  render: SuggestedPriceStepRender;
}

export type SuggestedPriceCalculationVariant = 'ai_generated' | 'overridden';

/**
 * The two shapes the drawer's "How is this Suggested Price calculated?" tiles can take, as plain
 * data so the section renders from `SUGGESTED_PRICE_CALCULATIONS.overridden` or
 * `SUGGESTED_PRICE_CALCULATIONS.ai_generated` without branching on variant in the markup.
 *
 * Both variants keep Negotiation Starting Price on the left; the middle and right tiles change title and
 * presentation once the buyer has overridden the row.
 */
export const SUGGESTED_PRICE_CALCULATIONS: Record<
  SuggestedPriceCalculationVariant,
  SuggestedPriceCalculationStep[]
> = {
  ai_generated: [
    { amount: 'negotiationStartingPrice', labelKey: 'negotiation_starting_price', render: 'currency' },
    { labelKey: 'ai_discount', percent: 'aiDiscount', render: 'percent' },
    { amount: 'aiValue', labelKey: 'suggested_price', render: 'currency' },
  ],
  overridden: [
    { amount: 'negotiationStartingPrice', labelKey: 'negotiation_starting_price', render: 'currency' },
    { amount: 'aiValue', labelKey: 'ai_suggested_price', percent: 'aiDiscount', render: 'stacked' },
    { amount: 'overrideValue', labelKey: 'your_override', percent: 'overrideDiscount', render: 'stacked' },
  ],
};
